import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import DashboardCard from "../components/DashboardCard";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  MarkerClusterer,
} from "@react-google-maps/api";
import { useSelector } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { useTranslation } from "react-i18next";
import Chart from "react-apexcharts";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

const Dashboard = () => {
  const [mylocation, setMylocation] = useState(null);
  const [locations, setLocations] = useState([]);
  const [dailygross, setDailygross] = useState(0);
  const [monthlygross, setMonthlygross] = useState(0);
  const [totalgross, setTotalgross] = useState(0);

  const [settings, setSettings] = useState({});
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const usersdata = useSelector((state) => state.usersdata);
  const bookinglistdata = useSelector((state) => state.bookinglistdata);
  const settingsdata = useSelector((state) => state.settingsdata);
  const auth = useSelector((state) => state.auth);
  const cars = useSelector((state) => state.cartypes.cars);
  const [allCompleteCount, setAllCompleteCount] = useState([]);
  const [allCancelCount, setAllCancelCount] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [driverCount, setDriverCount] = useState(0);
  const [customerCount, setCustomerCount] = useState(0);
  useEffect(() => {
    if (mylocation == null) {
      navigator.geolocation.getCurrentPosition(
        (position) =>
          setMylocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }),
        (error) => console.log(error)
      );
    }
  }, [mylocation]);

  useEffect(() => {
    if (settingsdata.settings) {
      setSettings(settingsdata.settings);
    }
  }, [settingsdata.settings]);

  useEffect(() => {
    if (usersdata.users) {
      const drivers = usersdata.users.filter(
        (user) =>
          user.usertype === "driver" &&
          user.approved === true &&
          user.driverActiveStatus === true &&
          user.licenseImage &&
          user.carApproved &&
          ((user.fleetadmin === auth.profile.uid &&
            auth.profile.usertype === "fleetadmin") ||
            auth.profile.usertype === "admin")
      );
      let locs = [];
      for (let i = 0; i < drivers.length; i++) {
        if (
          drivers[i].approved &&
          drivers[i].driverActiveStatus &&
          drivers[i].location &&
          cars
        ) {
          let carImage;
          for (let j = 0; j < cars.length; j++) {
            if (cars[j].name === drivers[i].carType) {
              carImage = cars[j].image;
            }
          }
          locs.push({
            id: i,
            lat: drivers[i].location.lat,
            lng: drivers[i].location.lng,
            drivername: drivers[i].firstName + " " + drivers[i].lastName,
            carnumber: drivers[i].vehicleNumber,
            cartype: drivers[i].carType,
            carImage: carImage,
          });
        }
      }
      setLocations(locs);
    }
  }, [usersdata.users, auth.profile.usertype, auth.profile.uid, cars]);

  useEffect(() => {
    if (bookinglistdata.bookings) {
      let today = new Date();
      let monthlyTotal = 0;
      let yearlyTotal = 0;
      let todayTotal = 0;
      for (let i = 0; i < bookinglistdata.bookings.length; i++) {
        if (
          (bookinglistdata.bookings[i].status === "PAID" ||
            bookinglistdata.bookings[i].status === "COMPLETE") &&
          ((bookinglistdata.bookings[i].fleetadmin === auth.profile.uid &&
            auth.profile.usertype === "fleetadmin") ||
            auth.profile.usertype === "admin")
        ) {
          const { tripdate, convenience_fees, driver_share, discount } =
            bookinglistdata.bookings[i];
          let tDate = new Date(tripdate);
          if (
            convenience_fees &&
            parseFloat(convenience_fees) > 0 &&
            auth.profile.usertype === "admin"
          ) {
            if (
              tDate.getDate() === today.getDate() &&
              tDate.getMonth() === today.getMonth() &&
              tDate.getFullYear() === today.getFullYear()
            ) {
              todayTotal =
                todayTotal +
                parseFloat(convenience_fees) -
                parseFloat(discount);
            }
            if (
              tDate.getMonth() === today.getMonth() &&
              tDate.getFullYear() === today.getFullYear()
            ) {
              monthlyTotal =
                monthlyTotal +
                parseFloat(convenience_fees) -
                parseFloat(discount);
            }
            yearlyTotal =
              yearlyTotal + parseFloat(convenience_fees) - parseFloat(discount);
          }
          if (
            driver_share &&
            parseFloat(driver_share) > 0 &&
            auth.profile.usertype === "fleetadmin"
          ) {
            if (
              tDate.getDate() === today.getDate() &&
              tDate.getMonth() === today.getMonth() &&
              tDate.getFullYear() === today.getFullYear()
            ) {
              todayTotal = todayTotal + parseFloat(driver_share);
            }
            if (
              tDate.getMonth() === today.getMonth() &&
              tDate.getFullYear() === today.getFullYear()
            ) {
              monthlyTotal = monthlyTotal + parseFloat(driver_share);
            }
            yearlyTotal = yearlyTotal + parseFloat(driver_share);
          }
        }
      }
      setDailygross(parseFloat(todayTotal).toFixed(settings.decimal));
      setMonthlygross(parseFloat(monthlyTotal).toFixed(settings.decimal));
      setTotalgross(parseFloat(yearlyTotal).toFixed(settings.decimal));
    }
  }, [
    bookinglistdata.bookings,
    settings,
    auth.profile.uid,
    auth.profile.usertype,
  ]);

  const barChartOptions = {
    labels: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    colors: ["#109496", "#f28e8e"],
    options: {
      maintainAspectRatio: false,
      scales: {
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
            },
          },
        ],
      },
      chart: {
        height: 350,
        type: "area",
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "smooth",
      },
    },
  };

  const barChart = [
    {
      name: t("completed_bookings"),
      data: allCompleteCount,
    },
    {
      name: t("cancelled_bookings"),
      data: allCancelCount,
    },
  ];
  useEffect(() => {
    if (usersdata.users) {
      let driverCount = 0;
      let activeCount = 0;
      let customerCount = 0;
      for (let i = 0; i < usersdata.users.length; i++) {
        if (usersdata.users[i].usertype === "driver") {
          if (
            (auth.profile.usertype === "fleetadmin" &&
              usersdata.users[i].fleetadmin === auth.profile.uid) ||
            auth.profile.usertype === "admin"
          ) {
            driverCount = driverCount + 1;
          }
        }
        if (usersdata.users[i].driverActiveStatus === true) {
          if (
            (auth.profile.usertype === "fleetadmin" &&
              usersdata.users[i].fleetadmin === auth.profile.uid) ||
            auth.profile.usertype === "admin"
          ) {
            activeCount = activeCount + 1;
          }
        }
        if (usersdata.users[i].usertype === "customer") {
          if (
            (auth.profile.usertype === "fleetadmin" &&
              usersdata.users[i].fleetadmin === auth.profile.uid) ||
            auth.profile.usertype === "admin"
          ) {
            customerCount = customerCount + 1;
          }
        }
      }
      setActiveCount(activeCount);
      setDriverCount(driverCount);
      setCustomerCount(customerCount);
    } else {
      setActiveCount(0);
      setDriverCount(0);
      setCustomerCount(0);
    }
  }, [usersdata.users, auth.profile]);

  useEffect(() => {
    let allCompleteCount = [];
    let allCancelCount = [];
    let months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    if (bookinglistdata.bookings) {
      for (let i = 0; i < months.length; i++) {
        let completeCount = 0;
        let cancelCount = 0;
        for (let j = 0; j < bookinglistdata.bookings.length; j++) {
          const { tripdate } = bookinglistdata.bookings[j];
          let tDate = new Date(tripdate);
          if (
            months[i] === months[tDate.getMonth()] &&
            (bookinglistdata.bookings[j].status === "PAID" ||
              bookinglistdata.bookings[j].status === "COMPLETE")
          ) {
            completeCount = completeCount + 1;
          }
          if (
            months[i] === months[tDate.getMonth()] &&
            bookinglistdata.bookings[j].status === "CANCELLED"
          ) {
            cancelCount = cancelCount + 1;
          }
        }
        allCompleteCount.push(completeCount);
        allCancelCount.push(cancelCount);
      }
    }
    setAllCompleteCount(allCompleteCount);
    setAllCancelCount(allCancelCount);
  }, [bookinglistdata.bookings]);

  return bookinglistdata.loading || usersdata.loading ? (
    <CircularLoading />
  ) : (
    <div>
      <Typography
        variant="h4"
        style={{
          margin: "20px 0px 10px 0",
          fontSize: 20,
          fontWeight: 500,
          textAlign: isRTL === "rtl" ? "right" : "left",
        }}
      >
        {t("gross_earning")}
      </Typography>
      <Grid container direction="row" spacing={2}>
        <Grid item xs style={{ textAlign: isRTL === "rtl" ? "right" : "left" }}>
          {settings.swipe_symbol === false ? (
            <DashboardCard
              crdStyle={{
                display: "flex",
                borderRadius: "16px",
                backgroundColor: "#fff",
                boxShadow: "rgba(0, 0, 0, 0.1) 0px -0.4rem 3rem",
              }}
              title={t("today_text")}
              image={require("../assets/img/money1.jpg").default}
            >
              {settings.symbol + " " + dailygross}
            </DashboardCard>
          ) : (
            <DashboardCard
              crdStyle={{
                display: "flex",
                borderRadius: "20px",
                backgroundColor: "#fff",
                boxShadow: "rgba(0, 0, 0, 0.1) 0px -0.4rem 3rem",
              }}
              title={t("today_text")}
              image={require("../assets/img/money1.jpg").default}
            >
              {dailygross + " " + settings.symbol}
            </DashboardCard>
          )}
        </Grid>
        <Grid item xs style={{ textAlign: isRTL === "rtl" ? "right" : "left" }}>
          {settings.swipe_symbol === false ? (
            <DashboardCard
              crdStyle={{
                display: "flex",
                borderRadius: "16px",
                backgroundColor: "#fff",
                boxShadow: "rgba(0, 0, 0, 0.1) 0px -0.4rem 3rem",
              }}
              title={t("this_month_text")}
              image={require("../assets/img/money2.jpg").default}
            >
              {settings.symbol + " " + monthlygross}
            </DashboardCard>
          ) : (
            <DashboardCard
              crdStyle={{
                display: "flex",
                borderRadius: "20px",
                backgroundColor: "#fff",
                boxShadow: "rgba(0, 0, 0, 0.1) 0px -0.4rem 3rem",
              }}
              title={t("this_month_text")}
              image={require("../assets/img/money2.jpg").default}
            >
              {monthlygross + " " + settings.symbol}
            </DashboardCard>
          )}
        </Grid>
        <Grid item xs style={{ textAlign: isRTL === "rtl" ? "right" : "left" }}>
          {settings.swipe_symbol === false ? (
            <DashboardCard
              crdStyle={{
                display: "flex",
                borderRadius: "16px",
                backgroundColor: "#fff",
                boxShadow: "rgba(0, 0, 0, 0.1) 0px -0.4rem 3rem",
              }}
              title={t("total")}
              image={require("../assets/img/money3.jpg").default}
            >
              {settings.symbol + " " + totalgross}
            </DashboardCard>
          ) : (
            <DashboardCard
              crdStyle={{
                display: "flex",
                borderRadius: "20px",
                backgroundColor: "#fff",
                boxShadow: "rgba(0, 0, 0, 0.1) 0px -0.4rem 3rem",
              }}
              title={t("total")}
              image={require("../assets/img/money3.jpg").default}
            >
              {totalgross + " " + settings.symbol}
            </DashboardCard>
          )}
        </Grid>
      </Grid>

      <Grid item xs={12} style={{ marginTop: 20 }}>
        <Grid container style={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}>
          <Grid
            item
            xs={12}
            sm={12}
            md={9}
            lg={9}
            style={{
              backgroundColor: "#fff",
              borderRadius: 20,
              boxShadow: "rgba(0, 0, 0, 0.1) 0px -0.4rem 3rem",
              overflow: "hidden",
            }}
            //boxShadow={3}
          >
            <Typography
              variant="h4"
              style={{
                margin: "15px 0px 10px 15px",
                fontSize: 20,
                fontWeight: 500,
                textAlign: isRTL === "rtl" ? "right" : "left",
              }}
            >
              {t("real_time_driver_section_text")}
            </Typography>
            {mylocation && mylocation.lat ? (
              <GoogleMap
                zoom={10}
                center={mylocation}
                mapContainerStyle={{ height: `380px` }}
              >
                <MarkerClusterer
                  options={{
                    imagePath:
                      "https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m",
                  }}
                >
                  {(clusterer) =>
                    locations.map((marker, index) => (
                      // <Marker
                      //   key={index}
                      //   position={marker}
                      //   clusterer={clusterer}
                      // />
                      <Marker
                        position={{ lat: marker.lat, lng: marker.lng }}
                        key={marker.id}
                        clusterer={clusterer}
                        icon={{
                          url: marker.carImage,
                          scaledSize: new window.google.maps.Size(35, 25),
                        }}
                      >
                        <InfoWindow
                          position={{ lat: marker.lat, lng: marker.lng }}
                          options={{
                            pixelOffset: new window.google.maps.Size(0, -32),
                          }}
                        >
                          <div>
                            {marker.drivername}
                            {/* {locations?.length} */}
                            <br />
                            {marker.carnumber}
                          </div>
                        </InfoWindow>
                      </Marker>
                    ))
                  }
                </MarkerClusterer>
                {/* {locations.map((marker) => (
                 
                ))} */}
              </GoogleMap>
            ) : null}
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            md={3}
            lg={3}
            style={{
              padding: 25,
              paddingTop: 0,
              paddingRight: 0,
              paddingBottom: 0,
              marginTop: "0px",
            }}
          >
            <Card
              style={{
                borderRadius: "16px",
                boxShadow: "rgba(0, 0, 0, 0.1) 0px -0.4rem 3rem",
                backgroundColor: "#fff0c1",
                minHeight: 120,
                marginBottom: 20,
                //  opacity: 0.8,
              }}
            >
              <Typography
                style={{
                  color: "#000",
                  fontWeight: 400,
                  textAlign: "center",
                  fontSize: 18,
                  marginTop: 25,
                }}
              >
                {t("total_cumtomer")}
              </Typography>
              <Typography
                style={{
                  color: "#000",
                  textAlign: "center",
                  marginTop: 8,
                  fontSize: 26,
                  fontWeight: "bold",
                }}
              >
                {customerCount}
              </Typography>
            </Card>
            <Card
              style={{
                borderRadius: "20px",
                boxShadow: "rgba(0, 0, 0, 0.1) 0px -0.4rem 3rem",
                backgroundColor: "#dadddf",
                minHeight: 120,
                marginBottom: 20,
                //  opacity: 0.8,
              }}
            >
              <Typography
                style={{
                  color: "#000",
                  fontWeight: 400,
                  textAlign: "center",
                  fontSize: 18,
                  marginTop: 25,
                }}
              >
                {t("total_drivers")}
              </Typography>
              <Typography
                style={{
                  color: "#000",
                  textAlign: "center",
                  marginTop: 8,
                  fontSize: 26,
                  fontWeight: "bold",
                }}
              >
                {driverCount}
              </Typography>
            </Card>
            <Card
              style={{
                borderRadius: "16px",
                boxShadow: "rgba(0, 0, 0, 0.1) 0px -0.4rem 3rem",
                backgroundColor: "#c4d9fc",
                minHeight: 120,
                marginBottom: 0,
                //  opacity: 0.8,
              }}
            >
              <Typography
                style={{
                  color: "#000",
                  fontWeight: 400,
                  textAlign: "center",
                  fontSize: 18,
                  marginTop: 25,
                }}
              >
                {t("active_driver")}
              </Typography>
              <Typography
                style={{
                  color: "#000",
                  textAlign: "center",
                  marginTop: 8,
                  fontSize: 26,
                  fontWeight: "bold",
                }}
              >
                {activeCount}
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Grid>
      <Box
        style={{
          padding: 10,
          marginTop: 20,
          borderRadius: 20,
          backgroundColor: "#fff",
          boxShadow: "rgba(0, 0, 0, 0.1) 0px -0.4rem 3rem",
        }}
        //boxShadow={6}
      >
        <Typography
          style={{
            margin: "15px 0px 10px 15px",
            fontSize: 20,
            fontWeight: 500,
          }}
          variant="h4"
        >
          {t("booking_chart")}
        </Typography>
        <Chart
          options={barChartOptions}
          series={barChart}
          type="area"
          width="100%"
          height={360}
        />
      </Box>
    </div>
  );
};

export default Dashboard;
